import { Pressable, StyleSheet, Text, View } from "react-native";

import type { UiTheme } from "../theme";
import { checkedAria } from "../a11y";
import { toggleRowStyles } from "./ToggleRow";

// A checkbox beside a sentence: the on/off sibling of ToggleRow for a choice
// that is a statement to agree with (#175), not a setting. Same contract: the
// WHOLE row is the tap target (role=checkbox, hitSlop on native, the 44pt floor
// on web via the shared row style), the checked state is announced AND drawn
// as a tick inside the box, never the box colour alone. Text uses the shared
// row title style, so it follows the system font scale.
export type CheckboxRowProps = {
  theme: UiTheme;
  label: string;
  value: boolean;
  onValueChange: (next: boolean) => void;
  disabled?: boolean;
};

const BOX = 24;

export function CheckboxRow({
  theme,
  label,
  value,
  onValueChange,
  disabled = false,
}: CheckboxRowProps) {
  const rowStyles = toggleRowStyles(theme);
  const { palette: p, typography: t, radius: r, spacing: sp } = theme;
  const box = StyleSheet.create({
    box: {
      width: BOX,
      height: BOX,
      borderRadius: r.r2,
      // A long label wraps: the box stays beside its first line.
      alignSelf: "flex-start",
      marginTop: sp.s1,
      borderWidth: 2,
      borderColor: value ? p.brand500 : p.ink3,
      backgroundColor: value ? p.brand500 : "transparent",
      alignItems: "center",
      justifyContent: "center",
    },
    tick: { color: p.inkOnBrand, fontSize: t.sizes.sm, fontWeight: "700" },
  });
  return (
    <Pressable
      accessibilityRole="checkbox"
      accessibilityLabel={label}
      accessibilityState={{ checked: value, disabled: disabled || undefined }}
      {...checkedAria(value, disabled)}
      disabled={disabled}
      onPress={() => onValueChange(!value)}
      hitSlop={8}
      style={rowStyles.row}
    >
      <View
        pointerEvents="none"
        accessibilityElementsHidden
        importantForAccessibility="no-hide-descendants"
        style={box.box}
      >
        {value ? <Text style={box.tick}>{"✓"}</Text> : null}
      </View>
      <View style={rowStyles.text}>
        <Text style={rowStyles.title}>{label}</Text>
      </View>
    </Pressable>
  );
}
